import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { X, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";

interface Props {
  proveedorId: string;
}

export default function ProveedorAliasChips({ proveedorId }: Props) {
  const queryClient = useQueryClient();

  const { data: aliases = [], isLoading } = useQuery({
    queryKey: ["proveedor-aliases", proveedorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proveedor_aliases")
        .select("id, nombre_ocr, created_at")
        .eq("proveedor_id", proveedorId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!proveedorId,
  });

  const deleteAlias = useMutation({
    mutationFn: async (aliasId: string) => {
      const { error } = await supabase
        .from("proveedor_aliases")
        .delete()
        .eq("id", aliasId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proveedor-aliases", proveedorId] });
      toast.success("Alias eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar alias");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-xs">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Cargando alias...</span>
      </div>
    );
  }

  if (aliases.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-medium">
        <Bot className="h-3 w-3" />
        Nombres Reconocidos (Alias OCR)
      </div>
      <div className="flex flex-wrap gap-1.5">
        {aliases.map((alias) => (
          <Badge
            key={alias.id}
            variant="secondary"
            className="text-[11px] h-6 pl-2 pr-1 gap-1 font-normal bg-secondary/60"
          >
            {alias.nombre_ocr}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                deleteAlias.mutate(alias.id);
              }}
              disabled={deleteAlias.isPending}
              className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
              aria-label={`Eliminar alias ${alias.nombre_ocr}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}
